import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Select } from 'antd';
import axiosInstance from '../../../axiosInstance';

const Shops = () => {
    const [shops, setShops] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [editingShop, setEditingShop] = useState(null);
    const [isUpdate, setIsUpdate] = useState(false);
    const [search, setSearch] = useState(''); // State to store search query

    useEffect(() => {
        fetchShops();
    }, [search]); // Fetch shops whenever the search term changes

    const fetchShops = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/shops', {
                params: {
                    search, // Send search query as a query parameter
                }
            });
            if (response.data.shops) {
                setShops(response.data.shops);
            } else {
                message.error('Failed to fetch shops.');
                setShops([]);
            }
            
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch shops.');
            setShops([]);
        } finally {
            setLoading(false);
        }
    };

    const showModal = (shop = null) => {
        if (shop) {
            setEditingShop(shop);
            setIsUpdate(true);
            form.setFieldsValue(shop);
        } else {
            setEditingShop(null);
            setIsUpdate(false);
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    // const handleOk = async (values) => {
    //     try {
    //         let response;
    //         if (editingShop) {
    //             response = await axiosInstance.put(`/shops/update/${editingShop.id}`, values);
    //             message.success('Shop updated successfully');
    //         } else {
    //             response = await axiosInstance.post('/shops/register', values);
    //             message.success('Shop added successfully');
    //         }
    //         fetchShops();
    //         setIsModalVisible(false);
    //         form.resetFields();
    //     } catch (error) {
    //         message.error('Failed to save shop.');
    //     }
    // };

    const handleOk = async (values) => {
        try {
            let response;
            if (editingShop) {
                response = await axiosInstance.put(`/shops/update/${editingShop.id}`, values);
                message.success('Shop updated successfully');
            } else {
                response = await axiosInstance.post('/shops/register', values);
                message.success('Shop added successfully');
            }
    
            fetchShops();
            setIsModalVisible(false);
            form.resetFields();
    
        } catch (error) {
            console.error('Error saving shop:', error);
    
            // Show detailed error message from the server if available
            if (error.response) {
                const msg = error.response.data?.message || 'Failed to save shop (server error).';
                message.error(msg);
            } else if (error.request) {
                message.error('No response from server. Please check your connection.');
            } else {
                message.error(`Error: ${error.message}`);
            }
        }
    };
    

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value); // Update search state when user types
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Owner Name',
            dataIndex: 'owner_name',
            key: 'owner_name',
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, shop) => (
                <Button onClick={() => showModal(shop)}>Edit</Button>
            ),
        },
    ];

    return (
        <div>

            {/* Search Input */}
            <div style={{ marginBottom: 16 }}>
                <Input
                    placeholder="Search by name, location, or description"
                    value={search}
                    onChange={handleSearchChange}
                    allowClear
                />
            </div>

            <div style={{ textAlign: 'right', marginBottom: 16 }}>
                <Button type="primary" onClick={() => showModal()}>
                    Add Shop
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={Array.isArray(shops) ? shops : []}
                rowKey="id"
                pagination={{ pageSize: 25 }}
                scroll={{ x: 'max-content' }}
                loading={loading}
            />

            <Modal
                title={editingShop ? 'Edit Shop' : 'Add New Shop'}
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                okText={editingShop ? 'Update' : 'Add'}
                cancelText="Cancel"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleOk}
                >
                    <Form.Item
                        name="type"
                        label="Type"
                        rules={[{ required: true, message: 'Please select the type!' }]}
                    >
                        <Select placeholder="Select type" disabled={isUpdate}>
                            <Select.Option value="shop">Shop</Select.Option>
                            <Select.Option value="warehouse">Warehouse</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please input the shop name!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="location"
                        label="Location"
                        rules={[{ required: true, message: 'Please input the shop location!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'Please input the shop description!' }]}
                    >
                        <Input.TextArea rows={4} />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label="Phone"
                        rules={[
                            { required: true, message: 'Please input the phone number!' },
                            { pattern: /^\d{10,15}$/, message: 'Please enter a valid phone number!' }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Please input the email address!' },
                            { type: 'email', message: 'Please enter a valid email address!' }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div >
    );
};

export default Shops;
